import * as React from 'react';
import {formatShare} from 'src/AnalyticsFlowView/flowModel';
import type {FlowNode} from 'src/AnalyticsFlowView/flowModel';

type Props = {
  sources: FlowNode[];
  dests: FlowNode[];
  /** Invoked for right-side nodes that carry a key (drillable groups). */
  onSelectDest?: (key: string) => void;
  formatValue: (cents: number) => string;
};

const CHART_H = 360;
const VIEW_W = 1000;
const NODE_W = 10;
const GAP = 7;
const TOP = 24;
const X_LEFT = 250;
const X_SPINE = 520;
const X_RIGHT = 770;
const MIN_NODE_H = 6;

// Node color by role. Spend nodes cycle the categorical palette in slot
// order (the ordering is CVD-validated; see globals.css); the semantic bands
// use fixed slots so they read the same in every window.
// 'spend-group' never appears on the payee drill (it is a tiered-overview
// construct), so it is excluded alongside 'spend'.
const KIND_COLOR: Record<
  Exclude<FlowNode['kind'], 'spend' | 'spend-group'>,
  string
> = {
  income: 'var(--chart-3)',
  'reserve-draw': 'var(--muted-foreground)',
  saved: 'var(--chart-3)',
  'take-home': 'var(--muted-foreground)',
  // Withheld pay is income that never arrived — a fixed slot distinct from
  // deposited income so the band reads as its own phenomenon.
  withheld: 'var(--chart-4)',
  // The unreviewed band, too, is an overview-only construct (the drill is
  // scoped to one category's allocated payments).
  unreviewed: 'var(--muted-foreground)',
  // Tier kinds live in the overview's TieredSankey; listed here only
  // because this map is exhaustive over FlowNodeKind.
  'taxes-tier': 'var(--chart-1)',
  'spending-tier': 'var(--muted-foreground)',
  'saving-tier': 'var(--chart-3)',
};

const nodeColor = (node: FlowNode, spendIndex: number): string =>
  node.kind === 'spend' || node.kind === 'spend-group'
    ? `var(--chart-${(spendIndex % 8) + 1})`
    : KIND_COLOR[node.kind];

type Laid = {
  node: FlowNode;
  y: number;
  h: number;
  /** Contiguous position on the spine (no gaps), for the ribbon ends. */
  spineY: number;
  /** The size the node draws at — compressed under a scale break. */
  drawnValue: number;
  color: string;
};

const drawnValueOf = (node: FlowNode): number =>
  node.displayValue ?? node.value;

const layout = (nodes: FlowNode[], scale: number): Laid[] => {
  let y = TOP;
  let spineY = TOP;
  let spendIndex = 0;
  return nodes.map((node) => {
    const drawnValue = drawnValueOf(node);
    const h = Math.max(MIN_NODE_H, drawnValue * scale);
    const laid: Laid = {
      node,
      y,
      h,
      spineY,
      drawnValue,
      color: nodeColor(node, node.kind === 'spend' ? spendIndex : 0),
    };
    if (node.kind === 'spend') {
      spendIndex += 1;
    }
    y += h + GAP;
    spineY += drawnValue * scale;
    return laid;
  });
};

type BreakGlyphProps = {
  x0: number;
  x1: number;
  y: number;
};

// The axis-break convention: a background-colored gap crossed by two
// slanted hairlines, laid over the node bar and the mouth of its ribbon.
const BreakGlyph = ({x0, x1, y}: BreakGlyphProps) => (
  <g>
    <line
      x1={x0}
      y1={y + 4}
      x2={x1}
      y2={y - 4}
      className="stroke-background"
      strokeWidth={9}
    />
    <line
      x1={x0}
      y1={y + 8}
      x2={x1}
      y2={y}
      className="stroke-muted-foreground"
      strokeWidth={1.5}
    />
    <line
      x1={x0}
      y1={y}
      x2={x1}
      y2={y - 8}
      className="stroke-muted-foreground"
      strokeWidth={1.5}
    />
  </g>
);

const ribbonPath = (
  x0: number,
  y0: number,
  h0: number,
  x1: number,
  y1: number,
  h1: number,
): string => {
  const mid = (x0 + x1) / 2;
  return [
    `M ${x0} ${y0}`,
    `C ${mid} ${y0}, ${mid} ${y1}, ${x1} ${y1}`,
    `L ${x1} ${y1 + h1}`,
    `C ${mid} ${y1 + h1}, ${mid} ${y0 + h0}, ${x0} ${y0 + h0}`,
    'Z',
  ].join(' ');
};

type NodeLabelProps = {
  laid: Laid;
  x: number;
  anchor: 'start' | 'end';
  formatValue: (cents: number) => string;
  /** The node's share of its side ("34%"), when meaningful. */
  share?: string | null;
};

// Long payee names would run off the right edge of the viewBox; the ribbon
// tooltip still carries the full name.
const truncate = (label: string, max = 24): string =>
  label.length > max ? `${label.slice(0, max - 1)}…` : label;

// Two lines when the node is tall enough, one compact line otherwise —
// short nodes stacked close together collide with two-line labels.
const NodeLabel = ({laid, x, anchor, formatValue, share}: NodeLabelProps) => {
  const {node, y, h} = laid;
  const mid = y + h / 2;
  const compressed = laid.drawnValue < node.value;
  const sublabel = compressed ? 'scale break' : node.sublabel;
  const value =
    formatValue(node.value) + (share != null ? ` · ${share}` : '');
  // The share lengthened the one-line form; compact labels shorten the
  // name to keep the whole line inside the label budget.
  const label = truncate(node.label, h < 30 && share != null ? 18 : 24);
  if (h >= 30) {
    return (
      <>
        <text
          x={x}
          y={mid - 1}
          textAnchor={anchor}
          className="fill-foreground text-[13px] font-semibold"
        >
          {label}
        </text>
        <text
          x={x}
          y={mid + 14}
          textAnchor={anchor}
          className="fill-muted-foreground text-[11.5px]"
        >
          {value}
          {sublabel ? ` · ${sublabel}` : ''}
        </text>
      </>
    );
  }
  return (
    <text
      x={x}
      y={mid + 4.5}
      textAnchor={anchor}
      className="fill-foreground text-[12.5px] font-semibold"
    >
      {label}{' '}
      <tspan className="fill-muted-foreground text-[11.5px] font-normal">
        · {value}
      </tspan>
    </text>
  );
};

/**
 * A two-stage sankey: sources merge into a central spine, the spine fans out
 * into destinations. Both sides are expected to sum to the same total (the
 * flow model guarantees it); ribbon and node heights are linear in value.
 */
export const FlowSankey = (props: Props) => {
  const {sources, dests, onSelectDest, formatValue} = props;

  const total = sources.reduce((sum, node) => sum + drawnValueOf(node), 0);
  if (total <= 0) {
    return (
      <div className="text-muted-foreground py-12 text-center text-sm">
        Nothing in this window.
      </div>
    );
  }
  const scale = CHART_H / total;
  // Share bases use TRUE values so a scale break can't distort them; each
  // side is its own base (the sides balance by construction).
  const sourcesTrue = sources.reduce((sum, node) => sum + node.value, 0);
  const destsTrue = dests.reduce((sum, node) => sum + node.value, 0);
  const shareSuffix = (value: number, base: number): string => {
    const share = formatShare(value, base);
    return share != null ? ` (${share})` : '';
  };
  const left = layout(sources, scale);
  const right = layout(dests, scale);
  // The gaps between nodes push each side past the spine's height, so the
  // viewBox follows whichever side's last node ends lowest.
  const bottomOf = (side: Laid[]): number => {
    const last = side.at(-1);
    return last == null ? TOP : last.y + last.h;
  };
  const height =
    Math.max(bottomOf(left), bottomOf(right), TOP + CHART_H) + 16;

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${VIEW_W} ${height}`}
        className="w-full min-w-[760px]"
        aria-label="Cash flow diagram"
      >
        {/* Ribbons under everything else */}
        {left.map((laid) => (
          <path
            key={`lr-${laid.node.kind}-${laid.node.key ?? laid.node.label}`}
            d={ribbonPath(
              X_LEFT + NODE_W,
              laid.y,
              laid.h,
              X_SPINE,
              laid.spineY,
              laid.drawnValue * scale,
            )}
            fill={laid.color}
            opacity={laid.node.kind === 'income' ? 0.35 : 0.45}
          >
            <title>{`${laid.node.label} — ${formatValue(laid.node.value)}`}</title>
          </path>
        ))}
        {right.map((laid) => (
          <path
            key={`rr-${laid.node.kind}-${laid.node.key ?? laid.node.label}`}
            d={ribbonPath(
              X_SPINE + NODE_W,
              laid.spineY,
              laid.drawnValue * scale,
              X_RIGHT,
              laid.y,
              laid.h,
            )}
            fill={laid.color}
            opacity={0.5}
          >
            <title>{`${laid.node.label} — ${formatValue(laid.node.value)}`}</title>
          </path>
        ))}

        {/* Spine */}
        <rect
          x={X_SPINE}
          y={TOP}
          width={NODE_W}
          height={total * scale}
          rx={2}
          className="fill-border"
        />

        {/* Left nodes */}
        {left.map((laid) => (
          <g key={`ln-${laid.node.kind}-${laid.node.key ?? laid.node.label}`}>
            <rect
              x={X_LEFT}
              y={laid.y}
              width={NODE_W}
              height={laid.h}
              rx={2}
              fill={laid.color}
            />
            <NodeLabel
              laid={laid}
              x={X_LEFT - 10}
              anchor="end"
              formatValue={formatValue}
              share={formatShare(laid.node.value, sourcesTrue)}
            />
            {laid.drawnValue < laid.node.value && (
              <BreakGlyph
                x0={X_LEFT - 5}
                x1={X_LEFT + NODE_W + 42}
                y={laid.y + laid.h * 0.62}
              />
            )}
          </g>
        ))}

        {/* Right nodes; drillable when the node carries a key */}
        {right.map((laid) => {
          const key = laid.node.key;
          const drillable = onSelectDest != null && key != null;
          return (
            <g
              key={`rn-${laid.node.kind}-${key ?? laid.node.label}`}
              onClick={drillable ? () => onSelectDest(key) : undefined}
              onKeyDown={
                drillable
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onSelectDest(key);
                      }
                    }
                  : undefined
              }
              role={drillable ? 'button' : undefined}
              tabIndex={drillable ? 0 : undefined}
              aria-label={
                drillable
                  ? `${laid.node.label}: ${formatValue(laid.node.value)}${shareSuffix(
                      laid.node.value,
                      destsTrue,
                    )} — break down`
                  : undefined
              }
              className={drillable ? 'cursor-pointer' : undefined}
            >
              <rect
                x={X_RIGHT}
                y={laid.y}
                width={NODE_W}
                height={laid.h}
                rx={2}
                fill={laid.color}
              />
              <NodeLabel
                laid={laid}
                x={X_RIGHT + 18}
                anchor="start"
                formatValue={formatValue}
                share={formatShare(laid.node.value, destsTrue)}
              />
              {laid.drawnValue < laid.node.value && (
                <BreakGlyph
                  x0={X_RIGHT - 42}
                  x1={X_RIGHT + NODE_W + 5}
                  y={laid.y + laid.h * 0.62}
                />
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};
